import { Component, OnInit } from '@angular/core'
import { ApiService, UserInfo } from '../../../services/api.service'
import { CommonModule } from '@angular/common'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { LinkComponent } from '@components/link/link.component'
import { HomeLinksService } from '../../../services/home-links.service'

export interface ActivtyCard {
    title: string
    action?: () => void
    buttonText: string
    background: boolean
    backgroundYellow?: boolean
    commisions?: boolean
    disabled?: boolean
}
@Component({
    selector: 'app-activity-block',
    templateUrl: './activity-block.component.html',
    imports: [CommonModule, TranslateModule, LinkComponent],
    standalone: true,
    styleUrl: './activity-block.component.scss',
})
export class ActivityBlock implements OnInit {
    userInfo: UserInfo
    activityCards: ActivtyCard[] = []
    hoveredCard: any = null
    currentStatus: string = ''
    commisionsState: string = ''

    constructor(
        private api: ApiService,
        private translate: TranslateService,
        private homeLinksService: HomeLinksService
    ) {}

    ngOnInit(): void {
        this.api.userInfo.subscribe((userInfo) => {
            this.userInfo = userInfo
            const role = userInfo.accountType as keyof ReturnType<typeof this.buildActivityBlocks>

            this.currentStatus = userInfo.loyaltyLevel || ''
            this.commisionsState = this.determineCommissionState()
            const CTAS = this.buildActivityBlocks() // ← construimos con this ya disponible
            this.activityCards = CTAS[role] || []
            console.log('Commissions state:', this.commisionsState)
        })
    }
    private buildActivityBlocks() {
        let loyaltyStatusNode = {
            title: this.translate.instant('home.deja_de_ser_pro_home'),
            action: () => this.homeLinksService.goToBenefits(true),
            buttonText: this.translate.instant('myrewards.link_know_your_benefits'),
            background: true,
        }
        return {
            FABRICATOR: [
                //OK
                loyaltyStatusNode,
                {
                    title: this.translate.instant('home.access_your_financial_portal'),
                    action: () => this.homeLinksService.goToFinancialPortal(),
                    buttonText: this.translate.instant('home.access'),
                    background: false,
                    disabled: this.userInfo.mainMenu.items.find((item) => item.code === 'my-financial-portal') ? false : true,
                },
            ],
            INSTALLER: [
                loyaltyStatusNode,
                {
                    title: this.translate.instant('home.find_technical_information'),
                    action: () => this.homeLinksService.goToTechnicalDocumentation(),
                    buttonText: this.translate.instant('home.access'),
                    background: false,
                },
                {
                    title: this.translate.instant('home.upload_your_rates'),
                    action: () => this.homeLinksService.goToMyRates(), //REVIEW: Esta va a la parte de especificar tarifas en perfil
                    buttonText: this.translate.instant('home.upload'),
                    background: false,
                    backgroundYellow: true,
                    disabled: !this.userInfo.mainMenu.items.find((item) => item.code === 'my-rates'),
                },
            ],
            DESIGNER: [
                {
                    title: this.determineCommissionsTitle(),
                    action: () => this.determineCommissionsAction(),
                    buttonText: this.determineCommissionsActionText(),
                    background: true,
                    comisions: true,
                    disabled: ['NO', 'MY'].includes(this.userInfo.country || ''),
                },
                {
                    title: this.translate.instant('home.specify_materials_and_start_earning_bonuses'),
                    action: () => this.homeLinksService.goToMyCommisions(),
                    buttonText: this.translate.instant('home.specify'),
                    background: false,
                    disabled: !this.userInfo.mainMenu.items.find((item) => item.code === 'my-bonuses'),
                },
                {
                    title: this.translate.instant('home.buy_discounted_products'),
                    action: () => this.homeLinksService.goToProjects(),
                    buttonText: this.translate.instant('home.go_to_my_orders'),
                    background: false,
                },
            ],
            RETAIL: [
                loyaltyStatusNode,
                {
                    title: this.translate.instant('home.register_your_sales_and_level_up'),
                    action: () => this.homeLinksService.goToSales(),
                    buttonText: this.translate.instant('my_actios.register_new_sale'),
                    background: false,
                    disabled: !this.userInfo.mainMenu.items.find((item) => item.code === 'my-sales'),
                },
                {
                    title: this.translate.instant('home.improve_your_showroom_request_a_new_display'),
                    action: () => this.homeLinksService.goToDisplays(),
                    buttonText: this.translate.instant('home.solicitar_exposicion_home'),
                    background: false,
                    disabled: !this.userInfo.mainMenu.items.find((item) => item.code === 'my-displays'),
                },
            ],
            DISTRIBUTOR: [
                loyaltyStatusNode,
                {
                    title: this.translate.instant('home.access_your_financial_portal'),
                    action: () => this.homeLinksService.goToFinancialPortal(),
                    buttonText: this.translate.instant('home.access'),
                    background: false,
                    disabled: this.userInfo.mainMenu.items.find((item) => item.code === 'my-financial-portal') ? false : true,
                },
            ],
            ARCHITECT: [],
        }
    }

    determineCommissionState(): string {
        const status = this.userInfo.supplierStatus || ''
        const hasSupplierId = !!this.userInfo.sapIdSupplier
        console.log('Determining commission state:', { status, hasSupplierId })

        if (!hasSupplierId && !status) return 'NO_PROJECT' // 1. No tiene proyectos ni bonificaciones
        if (status === 'Created from SAP') return 'INCOMPLETE_REGISTRATION' // 2. Registro no finalizado
        if (!hasSupplierId && status && status !== 'Created from SAP') return 'MUST_REGISTER' // 3. Tiene comisiones pero no se ha dado de alta
        if (['Blocked', 'Requested', 'In Review', 'In Progress'].includes(status)) return 'UNDER_REVIEW' // 4. Pendiente de revisión
        if (hasSupplierId) return 'ACTIVE_SUPPLIER' // 5. Dado de alta

        return 'UNKNOWN'
    }

    determineCommissionsTitle() {
        switch (this.commisionsState) {
            case 'NO_PROJECT':
                return this.translate.instant('home.start_your_project')
            case 'INCOMPLETE_REGISTRATION':
                return this.translate.instant('home.finish_your_registration_and_start_earning_bonifications')
            case 'MUST_REGISTER':
                return this.translate.instant('home.register_to_obtain_bonifications')
            case 'UNDER_REVIEW':
                return this.translate.instant('home.we_are_reviewing_your_register_data')
            case 'ACTIVE_SUPPLIER':
                return this.translate.instant('home.request_your_commision')
            default:
                return ''
        }
    }

    determineCommissionsAction() {
        switch (this.commisionsState) {
            case 'NO_PROJECT':
                return this.homeLinksService.goToProjects()
            case 'INCOMPLETE_REGISTRATION':
                return this.homeLinksService.goToSupplierPortalRegistration()
            case 'MUST_REGISTER':
                return this.homeLinksService.goToSupplierPortalRegistration()
            case 'UNDER_REVIEW':
                return null
            case 'ACTIVE_SUPPLIER':
                return this.homeLinksService.goToMyCommisions()
            default:
                return null
        }
    }

    determineCommissionsActionText() {
        switch (this.commisionsState) {
            case 'NO_PROJECT':
                return this.translate.instant('home.create_project')
            case 'INCOMPLETE_REGISTRATION':
                return this.translate.instant('home.continue_registration')
            case 'MUST_REGISTER':
                return this.translate.instant('home.register')
            case 'UNDER_REVIEW':
                return null
            case 'ACTIVE_SUPPLIER':
                return this.translate.instant('home.view_bonifications')
            default:
                return ''
        }
    }
    getCardClasses(card: ActivtyCard, index: number): string {
        let classes = ''
        // console.log(index   + ' index')
        // console.log(this.activityCards.filter((c) => !c.disabled).length)
        if (this.activityCards.filter(c => !c.disabled).length === 2) {
            classes += index == 0 ? 'sm:col-span-8 ' : 'sm:col-span-4 '
        } else if(this.activityCards.filter(c => !c.disabled).length === 1){
            classes += 'sm:col-span-12'
        }else {
            classes += 'sm:col-span-6 lg:col-span-4 '
        }
        if (card.backgroundYellow) {
            classes += 'background-semantic-warning '
        }
        return classes.trim()
    }
}
